import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  useDeliveryApi,
  useDeliveryRecipientsApi,
} from "hooks/use-delivery-api";
import { RefreshCw } from "lucide-react";
import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  sent: { label: "送信完了", cls: "badge-success" },
  // biome-ignore lint/style/useNamingConvention: matches backend DeliveryStatus enum value
  partially_sent: { label: "一部失敗", cls: "badge-warning" },
  failed: { label: "失敗", cls: "badge-error" },
  sending: { label: "送信中", cls: "badge-info" },
  scheduled: { label: "予約中", cls: "badge-accent" },
  draft: { label: "下書き", cls: "badge-ghost" },
  cancelled: { label: "キャンセル", cls: "badge-neutral" },
};

const RECIPIENT_STATUS: Record<string, { label: string; cls: string }> = {
  sent: { label: "送信", cls: "text-success" },
  pending: { label: "待機", cls: "text-base-content/60" },
  bounced: { label: "バウンス", cls: "text-error" },
  complained: { label: "苦情", cls: "text-error" },
  failed: { label: "失敗", cls: "text-error" },
};

export const DeliveryDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recipientPage, setRecipientPage] = useState(1);

  const { data: delivery, loading, error } = useDeliveryApi(id || "");
  const { data: recipientsData, loading: recipientsLoading } =
    useDeliveryRecipientsApi({
      deliveryId: id || "",
      page: recipientPage,
      limit: 50,
    });

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  if (error || !delivery) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="mb-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/deliveries")}
          >
            &larr; 戻る
          </Button>
        </div>
        <div className="alert alert-error">
          <span>{error?.message || "配信メールが見つかりません"}</span>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_BADGE[delivery.status] || {
    label: delivery.status,
    cls: "badge-ghost",
  };
  const recipientTotalPages = recipientsData
    ? Math.ceil(recipientsData.total / 50)
    : 1;

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/deliveries")}
        >
          &larr; 戻る
        </Button>
        <PageTitle>配信詳細</PageTitle>
        <span className={`badge ${statusInfo.cls}`}>{statusInfo.label}</span>
        <div className="ml-auto">
          <Button asChild variant="secondary" size="sm">
            <Link to={`/deliveries/new?copy-from=${id}`}>
              <RefreshCw size={16} />
              再配信
            </Link>
          </Button>
        </div>
      </div>

      {/* 基本情報 */}
      <div className="card bg-base-100 shadow-md mb-4">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-base-content/60">件名</div>
              <div className="font-medium">{delivery.subject}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">送信者</div>
              <div>{delivery.fromName || delivery.fromEmail}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">配信先グループ</div>
              <div>{delivery.groupName || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">
                {delivery.sentAt ? "配信日時" : "作成日時"}
              </div>
              <div>{formatDate(delivery.sentAt || delivery.createdAt)}</div>
            </div>
          </div>

          {/* 配信結果 */}
          {delivery.status !== "draft" && delivery.status !== "scheduled" && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm text-base-content/60 mb-2">配信結果</div>
              <div className="stats stats-horizontal shadow">
                <div className="stat px-4 py-2">
                  <div className="stat-title text-xs">成功</div>
                  <div className="stat-value text-lg text-success">
                    {delivery.successCount}
                  </div>
                </div>
                <div className="stat px-4 py-2">
                  <div className="stat-title text-xs">失敗</div>
                  <div className="stat-value text-lg text-error">
                    {delivery.failureCount}
                  </div>
                </div>
                <div className="stat px-4 py-2">
                  <div className="stat-title text-xs">合計</div>
                  <div className="stat-value text-lg">
                    {delivery.totalRecipients}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 本文 */}
      <div className="card bg-base-100 shadow-md mb-4">
        <div className="card-body">
          <h3 className="card-title text-base">本文</h3>
          <div className="bg-base-200 p-4 rounded-lg whitespace-pre-wrap text-sm max-h-80 overflow-y-auto">
            {delivery.bodyText}
          </div>
        </div>
      </div>

      {/* 宛先一覧 */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h3 className="card-title text-base">
            宛先一覧 ({recipientsData?.total || 0}件)
          </h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>会社名</th>
                  <th>担当者</th>
                  <th>メール</th>
                  <th>状態</th>
                  <th>送信日時</th>
                </tr>
              </thead>
              <tbody>
                {recipientsLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8">
                      <Loading size="md" />
                    </td>
                  </tr>
                ) : !recipientsData?.items.length ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 text-base-content/60"
                    >
                      宛先がありません
                    </td>
                  </tr>
                ) : (
                  recipientsData.items.map(r => {
                    const rStatus = RECIPIENT_STATUS[r.status] || {
                      label: r.status,
                      cls: "",
                    };
                    return (
                      <tr key={r.id}>
                        <td>{r.companyName || "-"}</td>
                        <td>{r.name || "-"}</td>
                        <td className="text-sm">{r.email}</td>
                        <td>
                          <span className={`font-medium ${rStatus.cls}`}>
                            {rStatus.label}
                          </span>
                          {r.errorMessage && (
                            <div className="text-xs text-error mt-1">
                              {r.errorMessage}
                            </div>
                          )}
                        </td>
                        <td className="text-sm">{formatDate(r.sentAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ページネーション */}
          {recipientTotalPages > 1 && (
            <div className="flex justify-center mt-4">
              <div className="join">
                <Button
                  type="button"
                  size="sm"
                  isJoinItem
                  disabled={recipientPage <= 1}
                  onClick={() => setRecipientPage(p => p - 1)}
                >
                  &laquo;
                </Button>
                <Button type="button" size="sm" isJoinItem>
                  {recipientPage} / {recipientTotalPages}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  isJoinItem
                  disabled={recipientPage >= recipientTotalPages}
                  onClick={() => setRecipientPage(p => p + 1)}
                >
                  &raquo;
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
